import type { OrganizationSnapshot } from './model';

export type EntityKind = 'position' | 'division' | 'team';
export interface EntitySelection {
  kind: EntityKind;
  id?: string;
  parentId?: string;
  divisionId?: string;
  focus?: 'people';
}
export interface StructureChange {
  key: string;
  kind: EntityKind | 'membership';
  id: string;
  name: string;
  status: 'added' | 'removed' | 'changed';
  details: string[];
}

/** Repair only the rendered graph. Saved relationships are never rewritten on read. */
export function reportingGraph(snapshot: OrganizationSnapshot) {
  const ids = new Set(snapshot.positions.map((p) => p.id));
  const parents = new Map<string, string | null>();
  const issues = new Map<string, string>();
  for (const role of snapshot.positions) {
    const parent = role.parentPositionId;
    if (parent && (!ids.has(parent) || parent === role.id)) {
      parents.set(role.id, null);
      issues.set(role.id, 'Needs a reporting line');
    } else parents.set(role.id, parent || null);
  }
  const complete = new Set<string>();
  for (const role of snapshot.positions) {
    if (complete.has(role.id)) continue;
    const trail: string[] = [];
    const indices = new Map<string, number>();
    let id: string | null = role.id;
    while (id && !complete.has(id)) {
      const index = indices.get(id);
      if (index !== undefined) {
        const cycle = trail.slice(index).sort();
        parents.set(cycle[0], null);
        cycle.forEach((key) => issues.set(key, 'Reporting cycle — review this role'));
        break;
      }
      indices.set(id, trail.length);
      trail.push(id);
      id = parents.get(id) || null;
    }
    trail.forEach((key) => complete.add(key));
  }
  for (const role of snapshot.positions) {
    const division = snapshot.divisions.find((d) => d.id === role.divisionId);
    const team = snapshot.teams.find((t) => t.id === role.teamId);
    const groupIssue =
      role.divisionId && !division
        ? 'Assigned division is unavailable'
        : role.teamId && !team
          ? 'Assigned team is unavailable'
          : team && (team.divisionId || null) !== (role.divisionId || null)
            ? 'Team and division do not match'
            : null;
    if (groupIssue)
      issues.set(role.id, [issues.get(role.id), groupIssue].filter(Boolean).join(' · '));
  }
  return { parents, issues };
}

export function groupRelationshipIssue(
  snapshot: OrganizationSnapshot,
  selection: EntitySelection
): string | undefined {
  if (!selection.id || selection.kind === 'position') return;
  const items = selection.kind === 'division' ? snapshot.divisions : snapshot.teams;
  const parentOf = (key: string): string | undefined =>
    selection.kind === 'division'
      ? snapshot.divisions.find((item) => item.id === key)?.parentDivisionId
      : snapshot.teams.find((item) => item.id === key)?.parentTeamId;
  let id: string | undefined = selection.id;
  const visited = new Set<string>();
  while (id) {
    if (visited.has(id)) return 'Group cycle — review the parent relationship';
    if (!items.some((item) => item.id === id)) return 'Parent group is unavailable';
    visited.add(id);
    id = parentOf(id);
  }
  if (selection.kind === 'team') {
    const team = snapshot.teams.find((item) => item.id === selection.id);
    if (team?.divisionId && !snapshot.divisions.some((division) => division.id === team.divisionId))
      return 'Assigned division is unavailable';
    const parent = snapshot.teams.find((item) => item.id === team?.parentTeamId);
    if (parent && (parent.divisionId || null) !== (team?.divisionId || null))
      return 'Parent team and division do not match';
  }
}

export function descendantIds(
  items: Array<{ id: string }>,
  parent: (id: string) => string | null | undefined,
  id: string
) {
  if (!id) return new Set<string>();
  const found = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of items) {
      if (!found.has(item.id) && found.has(parent(item.id) || '')) {
        found.add(item.id);
        changed = true;
      }
    }
  }
  return found;
}

export function selectedRoleIds(snapshot: OrganizationSnapshot, selection: EntitySelection | null) {
  if (!selection?.id) return new Set<string>();
  if (selection.kind === 'position') return new Set([selection.id]);
  if (selection.kind === 'division') {
    const divisions = descendantIds(
      snapshot.divisions,
      (id) => snapshot.divisions.find((d) => d.id === id)?.parentDivisionId,
      selection.id
    );
    return new Set(
      snapshot.positions
        .filter(
          (p) =>
            divisions.has(p.divisionId || '') ||
            divisions.has(snapshot.teams.find((t) => t.id === p.teamId)?.divisionId || '')
        )
        .map((p) => p.id)
    );
  }
  const teams = descendantIds(
    snapshot.teams,
    (id) => snapshot.teams.find((t) => t.id === id)?.parentTeamId,
    selection.id
  );
  return new Set(snapshot.positions.filter((p) => teams.has(p.teamId || '')).map((p) => p.id));
}

export function visibleRoleIds(
  snapshot: OrganizationSnapshot,
  search: string,
  group: string,
  collapsed: Set<string>
) {
  const { parents } = reportingGraph(snapshot);
  const term = search.trim().toLocaleLowerCase();
  const filter = group
    ? selectedRoleIds(snapshot, {
        kind: group.startsWith('division:') ? 'division' : 'team',
        id: group.split(':')[1],
      })
    : null;
  const matches = new Set(
    snapshot.positions
      .filter((p) => {
        const names = [
          p.title,
          p.department,
          snapshot.divisions.find((d) => d.id === p.divisionId)?.name,
          snapshot.teams.find((t) => t.id === p.teamId)?.name,
          ...p.assignments.map((a) => a.userName),
        ]
          .join(' ')
          .toLocaleLowerCase();
        return (!filter || filter.has(p.id)) && (!term || names.includes(term));
      })
      .map((p) => p.id)
  );
  // Search/group filtering temporarily reveals matched descendants and their ancestors.
  if (term || group) {
    const visible = new Set(matches);
    for (const id of matches) {
      let parent = parents.get(id);
      while (parent) {
        visible.add(parent);
        parent = parents.get(parent);
      }
    }
    return { visible, matches };
  }
  const visible = new Set<string>();
  for (const role of snapshot.positions) {
    let parent = parents.get(role.id);
    let hidden = false;
    while (parent) {
      if (collapsed.has(parent)) {
        hidden = true;
        break;
      }
      parent = parents.get(parent);
    }
    if (!hidden) visible.add(role.id);
  }
  return { visible, matches };
}

const sorted = (values: string[]) => [...values].sort().join('|');
const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, nested]) => [key, stableValue(nested)])
    );
  return value;
};
const text = (value: unknown): string =>
  value == null
    ? ''
    : typeof value === 'object'
      ? JSON.stringify(stableValue(value))
      : String(value);

export function compareStructures(
  before: OrganizationSnapshot,
  after: OrganizationSnapshot
): StructureChange[] {
  const changes: StructureChange[] = [];
  const compare = (
    kind: EntityKind,
    left: any[],
    right: any[],
    fields: Array<[string, string]>
  ) => {
    const old = new Map(left.map((item) => [item.id, item]));
    const next = new Map(right.map((item) => [item.id, item]));
    for (const id of new Set([...old.keys(), ...next.keys()])) {
      const a = old.get(id),
        b = next.get(id);
      const name = b?.title || b?.name || a?.title || a?.name;
      if (!a || !b) {
        changes.push({
          key: `${kind}:${id}`,
          kind,
          id,
          name,
          status: a ? 'removed' : 'added',
          details: [],
        });
        continue;
      }
      const details = fields
        .filter(([field]) => text(a[field]) !== text(b[field]))
        .map(([field, label]) => {
          if (field === 'name' || field === 'title') return `${label}: ${a[field]} → ${b[field]}`;
          return label;
        });
      if (
        kind === 'position' &&
        sorted(a.assignments.map((x: any) => `${x.userId}:${x.isPrimary}`)) !==
          sorted(b.assignments.map((x: any) => `${x.userId}:${x.isPrimary}`))
      )
        details.push('Occupants changed');
      if (details.length)
        changes.push({ key: `${kind}:${id}`, kind, id, name, status: 'changed', details });
    }
  };
  compare('position', before.positions, after.positions, [
    ['title', 'Renamed'],
    ['parentPositionId', 'Reporting line changed'],
    ['divisionId', 'Division changed'],
    ['teamId', 'Team changed'],
    ['description', 'Responsibilities changed'],
    ['department', 'Department changed'],
  ]);
  compare('division', before.divisions, after.divisions, [
    ['name', 'Renamed'],
    ['parentDivisionId', 'Parent division changed'],
    ['managerId', 'Manager changed'],
    ['description', 'Responsibilities changed'],
    ['type', 'Division type changed'],
    ['code', 'Code changed'],
    ['color', 'Colour changed'],
    ['budget', 'Budget changed'],
    ['headcount', 'Planned headcount changed'],
    ['sortOrder', 'Order changed'],
    ['metadata', 'Details changed'],
  ]);
  compare('team', before.teams, after.teams, [
    ['name', 'Renamed'],
    ['parentTeamId', 'Parent team changed'],
    ['divisionId', 'Division changed'],
    ['leadId', 'Team lead changed'],
    ['description', 'Responsibilities changed'],
    ['type', 'Team type changed'],
    ['code', 'Code changed'],
    ['sortOrder', 'Order changed'],
    ['settings', 'Settings changed'],
    ['metadata', 'Details changed'],
  ]);
  const memberKeys = (s: OrganizationSnapshot) =>
    new Map(s.teamMemberships.map((m) => [`${m.teamId}:${m.userId}`, m]));
  const left = memberKeys(before),
    right = memberKeys(after);
  for (const key of new Set([...left.keys(), ...right.keys()])) {
    const a = left.get(key),
      b = right.get(key);
    if (a && b && a.role === b.role) continue;
    const membership = b || a!;
    const team = (b ? after : before).teams.find((t) => t.id === membership.teamId)?.name || 'Team';
    changes.push({
      key: `membership:${key}`,
      kind: 'membership',
      id: membership.teamId,
      name: `${membership.userName} · ${team}`,
      status: !a ? 'added' : !b ? 'removed' : 'changed',
      details: a && b ? [`Team role: ${a.role} → ${b.role}`] : ['Team membership'],
    });
  }
  return changes.sort((a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name));
}
